import type { TimesheetEntry } from './timesheet';

export interface WorkInterval {
  start: number;
  end: number;
}

export function mergeIntervals(values: WorkInterval[]): WorkInterval[] {
  const sorted = values
    .filter((v) => Number.isFinite(v.start) && Number.isFinite(v.end) && v.end > v.start)
    .sort((a, b) => a.start - b.start);
  const result: WorkInterval[] = [];
  for (const value of sorted) {
    const last = result[result.length - 1];
    if (last && value.start <= last.end) last.end = Math.max(last.end, value.end);
    else result.push({ ...value });
  }
  return result;
}

export function subtractIntervals(work: WorkInterval[], breaks: WorkInterval[]): WorkInterval[] {
  let result = mergeIntervals(work);
  for (const pause of mergeIntervals(breaks)) {
    result = result.flatMap((v) => {
      if (pause.end <= v.start || pause.start >= v.end) return [v];
      return [
        { start: v.start, end: Math.min(v.end, pause.start) },
        { start: Math.max(v.start, pause.end), end: v.end },
      ].filter((p) => p.end > p.start);
    });
  }
  return result;
}

export function intervalMinutes(values: WorkInterval[]): number {
  return values.reduce((n, v) => n + (v.end - v.start) / 60000, 0);
}

/** Explicit periods are clipped to work and unioned; gaps never become work/breaks. */
export function registeredIntervals(entries: TimesheetEntry[]) {
  const work = mergeIntervals(
    entries.map((e) => ({ start: Date.parse(e.checkInAt), end: Date.parse(e.checkOutAt ?? '') })),
  );
  const pauses: WorkInterval[] = [];
  for (const e of entries) {
    const start = Date.parse(e.checkInAt);
    const end = Date.parse(e.checkOutAt ?? '');
    if (!(end > start)) continue;
    if (e.breakPeriods?.length) {
      for (const p of e.breakPeriods)
        pauses.push({
          start: Math.max(start, Date.parse(p.startAt)),
          end: Math.min(end, Date.parse(p.endAt ?? '')),
        });
    } else if (e.breakMinutes > 0) {
      // Legacy duration-only pause has no observed position: allocate at the end.
      pauses.push({ start: Math.max(start, end - e.breakMinutes * 60000), end });
    }
  }
  const net = subtractIntervals(work, pauses);
  return { work, net, pauses: subtractIntervals(work, net) };
}

/** Select a duration along registered work only; never count the gaps. */
export function intervalSlice(
  intervals: WorkInterval[],
  skipMinutes: number,
  minutes: number,
): WorkInterval[] {
  const result: WorkInterval[] = [];
  let skip = Math.max(0, skipMinutes) * 60000;
  let left = Math.max(0, minutes) * 60000;
  for (const interval of intervals) {
    const duration = interval.end - interval.start;
    if (skip >= duration) {
      skip -= duration;
      continue;
    }
    const start = interval.start + skip;
    skip = 0;
    const end = Math.min(interval.end, start + left);
    if (end > start) result.push({ start, end });
    left -= end - start;
    if (left <= 0) break;
  }
  return result;
}

/**
 * Actual break periods retain their location. A duration-only additional
 * deduction is an assumption at the configured work threshold, clipped to
 * registered work. This never asserts an observed break in an unrecorded gap.
 */
export function workAfterUnpaidMinutes(
  entries: TimesheetEntry[],
  totalMinutes: number,
  thresholdMinutes: number,
) {
  const registered = registeredIntervals(entries);
  const total = Math.min(intervalMinutes(registered.work), Math.max(0, totalMinutes));
  const recorded = intervalSlice(registered.pauses, 0, total);
  const net = subtractIntervals(registered.work, recorded);
  const extra = total - intervalMinutes(recorded);
  const assumed = intervalSlice(
    net,
    Math.min(thresholdMinutes, Math.max(0, intervalMinutes(net) - extra)),
    extra,
  );
  return { net: subtractIntervals(net, assumed), recorded, assumed };
}
